import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { certificatesApi } from '../../api/services/certificates.api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DialogClose, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { Link2 } from 'lucide-react';

const linkBatchSchema = z.object({
  batchNumber: z.string().min(1, 'Batch number is required'),
  itemId: z.string().min(1, 'Item is required'),
  quantity: z.coerce.number().min(0.01, 'Quantity must be greater than 0'),
  heatNumber: z.string().optional(),
  lotNumber: z.string().optional(),
  notes: z.string().optional(),
});

type LinkBatchFormData = z.infer<typeof linkBatchSchema>;

interface LinkBatchFormProps {
  certificateId: string;
  onSuccess?: () => void;
}

export function LinkBatchForm({ certificateId, onSuccess }: LinkBatchFormProps) {
  const queryClient = useQueryClient();

  const form = useForm<LinkBatchFormData>({
    resolver: zodResolver(linkBatchSchema),
    defaultValues: {
      quantity: 1,
    },
  });

  // Fetch inventory items for selection
  const { data: itemsData } = useQuery({
    queryKey: ['inventory', 'items'],
    queryFn: async () => {
      // Using the inventory API - adjust if needed
      const response = await fetch('http://localhost:3000/api/inventory?limit=1000', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('weatherford_access_token')}`,
        },
      });
      return response.json();
    },
  });

  const linkBatchMutation = useMutation({
    mutationFn: async (data: LinkBatchFormData) => {
      return certificatesApi.linkBatch(certificateId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificate', certificateId] });
      queryClient.invalidateQueries({ queryKey: ['certificates'] });
      toast.success('Batch linked to certificate successfully');
      form.reset();
      onSuccess?.();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to link batch');
    },
  });

  const onSubmit = (data: LinkBatchFormData) => {
    linkBatchMutation.mutate(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <DialogHeader>
          <DialogTitle>Link Batch to Certificate</DialogTitle>
          <DialogDescription>
            Associate an inventory batch with this material certificate for traceability
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormField
            control={form.control}
            name="batchNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Batch Number *</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., BATCH-2024-001" {...field} />
                </FormControl>
                <FormDescription>The unique identifier for this inventory batch</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="itemId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Material / Item *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select item" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {itemsData?.data?.map((item: any) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} ({item.itemNumber || item.sku})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="quantity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Quantity *</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min="0.01" {...field} />
                </FormControl>
                <FormDescription>Quantity covered by this certificate</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="heatNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Heat Number (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., H123456" {...field} />
                  </FormControl>
                  <FormDescription>Steel heat number</FormDescription>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lotNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lot Number (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., LOT-2024-001" {...field} />
                  </FormControl>
                  <FormDescription>Batch/lot identifier</FormDescription>
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes (Optional)</FormLabel>
                <FormControl>
                  <Input placeholder="Additional notes..." {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end gap-3">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={linkBatchMutation.isPending}>
            {linkBatchMutation.isPending ? (
              <>
                <Link2 className="h-4 w-4 mr-2 animate-spin" />
                Linking...
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-2" />
                Link Batch
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
